import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import { useIntl } from '@mfw/components';
import Box from '@mui/material/Box';

import type { ApiMembershipTermsOfUse } from '../../schemas/termsOfUse.schema';

import { getTermsOfUseQuestionTitle } from './termsOfUseQuestionTitle';
import { TermsOfUseDisclaimer } from './TermsOfUseDisclaimer';
import messages from './messages';

export interface ConnectionTermsOfUseConsentProps {
  termsOfUse: ApiMembershipTermsOfUse;
  organizationName: string;
  checkedVersionId: string | null;
  onChange: (versionId: string | null) => void;
  'data-testid'?: string;
}

/**
 * 6.10 — the organization's terms an invited client accepts before first
 * entering, when the organization asks for it on connecting. Renders the
 * snapshot it is handed (the signed-in modal and the pre-session accept page
 * both receive it with the invitation) and reports the version id the box
 * covers.
 */
export const ConnectionTermsOfUseConsent = ({
  termsOfUse,
  organizationName,
  checkedVersionId,
  onChange,
  'data-testid': dataTestId,
}: ConnectionTermsOfUseConsentProps) => {
  const { formatMessage } = useIntl();
  const version = termsOfUse.version;
  if (!version) return null;

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      p={2}
      border={1}
      borderColor="divider"
      borderRadius={2}
    >
      <Typography variant="subtitle1">
        {formatMessage(messages.connectionTitle, { organizationName })}
      </Typography>
      {termsOfUse.questions.map((question) => (
        <Box key={question.id}>
          <Typography variant="subtitle2">
            {getTermsOfUseQuestionTitle(
              {
                questionKey: question.key,
                questionText: question.questionText,
              },
              formatMessage,
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {
              termsOfUse.answers.find(
                (answer) => answer.questionId === question.id,
              )?.textValue
            }
          </Typography>
        </Box>
      ))}
      <TermsOfUseDisclaimer />
      <FormControlLabel
        sx={{ alignItems: 'flex-start' }}
        control={
          <Checkbox
            data-testid={dataTestId}
            checked={checkedVersionId === version.id}
            onChange={(_e, checked) => onChange(checked ? version.id : null)}
            sx={{ pt: 0 }}
          />
        }
        label={formatMessage(messages.connectionAcceptLabel)}
      />
    </Box>
  );
};
